import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { syncRoomFromApiToFirebase, useGetMessages, useGetRooms, ensureUserInPublicRoom, ensureCanonicalPublicRoomFlag } from 'store/services/firebase-broadcast';
import { ref as dbRef, get } from 'firebase/database';
import { db, storage } from 'utils/firebase-config';
import { ref, getDownloadURL, getMetadata, listAll } from 'firebase/storage';
import { userIsRoomMember, isPublicRoom, publicRoomTipStorageKey, pickCanonicalPublicRoom } from 'helpers/utils';
import {
  isPhoneViewport,
  isTabletOrBelowViewport,
} from 'helpers/viewportBreakpoints';
import { toast } from 'react-toastify';
import { flushChatRoomPresence, startChatRoomPresence } from 'helpers/chatRoomPresence';

export function useChatRoomPanel() {
  const [showReplies, setShowReplies] = useState(false);
  const [activateRoom, setActiveRoom] = useState<any>(null);
  const [chatInfo, setChatInfo] = useState(false);
  const [showThreads, setShowThreads] = useState<any>({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isPhone, setIsPhone] = useState(isPhoneViewport());
  /** Tablet + phone: drawer panels for ChatInfo / MessageThread. */
  const [isMobile, setIsMobile] = useState(isTabletOrBelowViewport());
  const location: any = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const preserveSelection = Boolean(location?.state?.preserveSelection && location?.state?.selected?.pk);
  const [selected, setSelected] = useState<any>(
    preserveSelection ? location.state.selected : {}
  );
  const [isJoined, setIsJoined] = useState<boolean>(false);
  // Bumped to force a re-read of the room's Storage folder (e.g. right after an
  // upload) — Storage has no real-time listener, so the media list is pull-based.
  const [mediaRefreshKey, setMediaRefreshKey] = useState(0);
  const refreshMedia = () => setMediaRefreshKey((k) => k + 1);
  const lastMediaRoomRef = useRef<any>(null);
  const [room, setRoom] = useState<any>();
  const { pk }: any = selected ?? {};
  const hasAutoSelectedRef = useRef(preserveSelection);

  useEffect(() => {
    // Only keep router-carried rooms when navigation explicitly asks to preserve
    // (expand/collapse, create-room, nearby open). Stale state.selected alone
    // must not block Welcome as the default landing preview.
    if (location?.state?.preserveSelection && location?.state?.selected?.pk) {
      setSelected(location.state.selected);
      hasAutoSelectedRef.current = true;
    }
  }, [location?.state?.selected, location?.state?.preserveSelection]);

  useEffect(() => {
    const handleResize = () => {
      const phone = isPhoneViewport();
      setIsPhone(phone);
      setIsMobile(isTabletOrBelowViewport());
      if (!phone) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleThreads = (e: any) => {
    setShowThreads(e);
  };

  // Pass the room id straight through. Laundering it via state added a render where the
  // hook still held the previous room, so its history flashed on switch.
  const { chat, isLoading, hasMore, isLoadingMore, loadOlderMessages, isInitialized } =
    useGetMessages(pk);
  const allRooms = useGetRooms();

  // Re-read the room's Storage media whenever attachment-bearing messages change.
  // Includes thread-reply attachments nested under each parent — Storage has no
  // realtime listener, so the live `chat` list is our trigger.
  const attachmentSig = useMemo(() => {
    const ids: string[] = [];
    (chat || []).forEach((m: any) => {
      if (m?.attachments?.[0]?.id) ids.push(String(m.attachments[0].id));
      const threads = Array.isArray(m?.threads)
        ? m.threads
        : m?.threads && typeof m.threads === 'object'
          ? Object.values(m.threads)
          : [];
      threads.forEach((t: any) => {
        if (t?.attachments?.[0]?.id) ids.push(String(t.attachments[0].id));
      });
    });
    return ids.sort().join('|');
  }, [chat]);

  const prevSigRef = useRef<{ pk: any; sig: string } | null>(null);
  useEffect(() => {
    const prev = prevSigRef.current;
    // On a new room (or first load) just establish the baseline — the media
    // effect's own listAll already covers it. Only an in-room change refreshes.
    if (!prev || prev.pk !== pk) {
      prevSigRef.current = { pk, sig: attachmentSig };
      return;
    }
    if (prev.sig !== attachmentSig) {
      prevSigRef.current = { pk, sig: attachmentSig };
      refreshMedia();
    }
  }, [attachmentSig, pk]);

  // Hardening: when a media upload lands on a parent *outside* the loaded
  // message window, attachmentSig can't see it. sendFCMMessage / sendThreadMessage
  // bump rooms/{id}/mediaVersion for every attachment send — watch that via the
  // rooms listener so Chat Info still refreshes for every member.
  const mediaVersion =
    typeof selected?.mediaVersion === 'number' ? selected.mediaVersion : null;
  const prevMediaVersionRef = useRef<{ pk: any; version: number | null } | null>(null);
  useEffect(() => {
    const prev = prevMediaVersionRef.current;
    if (!prev || prev.pk !== pk) {
      prevMediaVersionRef.current = { pk, version: mediaVersion };
      return;
    }
    if (mediaVersion != null && mediaVersion !== prev.version) {
      prevMediaVersionRef.current = { pk, version: mediaVersion };
      refreshMedia();
    } else {
      prevMediaVersionRef.current = { pk, version: mediaVersion };
    }
  }, [mediaVersion, pk]);

  useEffect(() => {
    if (hasAutoSelectedRef.current || !allRooms || allRooms.length === 0) {
      return;
    }

    // Intentional carry-over already applied in the location effect.
    if (location?.state?.preserveSelection && location?.state?.selected?.pk) {
      hasAutoSelectedRef.current = true;
      return;
    }

    const firstRoom = pickCanonicalPublicRoom(allRooms) ?? allRooms[0];
    if (firstRoom && firstRoom.pk) {
      setSelected(firstRoom);
      hasAutoSelectedRef.current = true;
    }
  }, [allRooms, location?.state?.selected, location?.state?.preserveSelection]);

  useEffect(() => {
    if (!selected?.pk || !allRooms?.length) {
      return;
    }

    const liveRoom = allRooms.find((room: any) => room.pk === selected.pk);
    if (!liveRoom) {
      return;
    }

    setSelected((prevSelected: any) => {
      if (!prevSelected || prevSelected.pk !== liveRoom.pk) {
        return prevSelected;
      }
      return {
        ...prevSelected,
        ...liveRoom,
        attachments: prevSelected.attachments,
        media: prevSelected.media,
      };
    });
  }, [allRooms, selected?.pk]);


  useEffect(() => {
    if (chat.length > 0 && showThreads.id) {
      const threadMessage = chat.find((i: any) => i.id === showThreads.id);
      if (threadMessage) {
        setShowThreads(threadMessage);
      }
    }
  }, [chat, showThreads.id]);

  useEffect(() => {
    if (!selected) {
      setIsJoined(false);
      return;
    }

    setIsJoined(userIsRoomMember(selected, user?.pk));
  }, [selected, user?.pk]);

  // Wall-clock chat presence → same RoomLoggedTime sum as video (rooms/logged-time).
  useEffect(() => {
    if (selected?.pk && isJoined) {
      startChatRoomPresence(selected.pk);
    } else {
      flushChatRoomPresence();
    }

    const onPageHide = () => flushChatRoomPresence();
    window.addEventListener('pagehide', onPageHide);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      flushChatRoomPresence();
    };
  }, [selected?.pk, isJoined]);

  // Public rooms: auto-join on enter (no host approval). Also shows a one-time tip.
  const autoJoiningRef = useRef<number | string | null>(null);
  useEffect(() => {
    if (!selected?.pk || !user?.pk) return;
    if (!isPublicRoom(selected, allRooms)) return;
    if (userIsRoomMember(selected, user.pk)) return;
    if (autoJoiningRef.current === selected.pk) return;

    autoJoiningRef.current = selected.pk;
    let cancelled = false;

    ensureUserInPublicRoom(selected, user, allRooms)
      .then(() => {
        if (cancelled) return;
        setIsJoined(true);
        setSelected((prev: any) => {
          if (!prev || prev.pk !== selected.pk) return prev;
          const participants = Array.isArray(prev.participants)
            ? prev.participants
            : Object.values(prev.participants || {});
          const already = participants.some((p: any) => p?.pk === user.pk);
          if (already) return { ...prev, is_public: true };
          const { access, refresh, ...userData } = user;
          return {
            ...prev,
            is_public: true,
            participants: [...participants.filter((p: any) => p != null), userData],
          };
        });
      })
      .catch((error) => {
        if (error?.code === 'INVALID_USER_DATA') return;
        console.error('Failed to auto-join public room:', error);
      })
      .finally(() => {
        if (autoJoiningRef.current === selected.pk) {
          autoJoiningRef.current = null;
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selected?.pk, selected?.topic, selected?.is_public, selected?.participants, user?.pk, allRooms]);

  // One-time tip when the user first successfully opens a public room.
  useEffect(() => {
    if (!selected?.pk || !isPublicRoom(selected, allRooms) || !isJoined) return;
    const key = publicRoomTipStorageKey(selected.pk);
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
      toast.info('This is a public room — you can chat right away.');
    } catch {
      // localStorage may be unavailable; tip is best-effort.
    }
  }, [selected?.pk, selected?.topic, selected?.is_public, isJoined, allRooms]);

  // Keep exactly one Welcome-topic room marked public when the room list updates.
  const publicFlagSyncedRef = useRef(false);
  useEffect(() => {
    if (!allRooms?.length || publicFlagSyncedRef.current) return;
    publicFlagSyncedRef.current = true;
    ensureCanonicalPublicRoomFlag(allRooms).catch((error) => {
      publicFlagSyncedRef.current = false;
      if (error?.code === 'INVALID_USER_DATA') return;
      console.error('Failed to sync canonical public room flag:', error);
    });
  }, [allRooms]);

  useEffect(() => {
    if (selected && Object.keys(selected).length > 0) {
      const roomRef = dbRef(db, `rooms/${selected?.pk}`);
      get(roomRef)
        .then((snapshot) => {
          setRoom(snapshot.val());
        })
        .catch((error) => {
          console.error('Error loading room:', error);
        });
    }
  }, [selected]);

  useEffect(() => {
    if (room === null && selected?.pk && selected?.host?.pk) {
      syncRoomFromApiToFirebase(selected).catch((error) => {
        console.error('Failed to sync room to Firebase:', error);
      });
    }
  }, [room, selected]);

  useEffect(() => {
    if (selected?.pk) {
      // Clear the media only when the room actually changes — not on a manual
      // refresh of the same room, which would blank the grid before it repopulates.
      // Also close the thread panel only on real room switches; closing it on every
      // mediaRefreshKey bump would unmount MessageThread right after a thread upload.
      if (lastMediaRoomRef.current !== selected.pk) {
        lastMediaRoomRef.current = selected.pk;
        setShowReplies(false);
        setSelected((prevSelected: any) => ({
          ...prevSelected,
          attachments: [],
          media: [],
        }));
      }

      if (isJoined) {
        const currentRoomId = selected.pk;
        const listRef = ref(storage, `/${currentRoomId}`);

        listAll(listRef)
          .then(async (res) => {
            if (!selected || selected.pk !== currentRoomId) {
              return;
            }

            if (res.items && res.items.length > 0) {
              // Friendly filename lookup from the loaded messages. A storage object's
              // name equals the message's attachments[0].id, so we can recover the
              // original name (e.g. "image3.jpg") for existing uploads.
              // Also include thread-reply attachment names when the parent is loaded.
              const nameById = new Map<string, string>();
              (chat || []).forEach((m: any) => {
                const a = m?.attachments?.[0];
                if (a?.id && a?.name) nameById.set(a.id, a.name);
                const threadList = Array.isArray(m?.threads)
                  ? m.threads
                  : m?.threads && typeof m.threads === 'object'
                    ? Object.values(m.threads)
                    : [];
                threadList.forEach((t: any) => {
                  const ta = t?.attachments?.[0];
                  if (ta?.id && ta?.name) nameById.set(ta.id, ta.name);
                });
              });

              // Resolve each stored object's URL *and* its metadata. The storage path
              // has no file extension, so contentType is the only reliable way to tell
              // photos from videos; timeCreated lets us order newest-upload-first
              // (listAll itself returns objects sorted by name, not by time).
              const withMeta = await Promise.all(
                res.items.map(async (itemRef) => {
                  const [url, meta] = await Promise.all([
                    getDownloadURL(itemRef),
                    getMetadata(itemRef).catch(() => null),
                  ]);
                  const type = (meta?.contentType || '').startsWith('video/') ? 'video' : 'image';
                  const time = meta?.timeCreated ? new Date(meta.timeCreated).getTime() : 0;
                  // Prefer custom metadata (set on new uploads; survives even when the
                  // message isn't loaded), fall back to the message name for older files.
                  const name = meta?.customMetadata?.originalName || nameById.get(itemRef.name);
                  return { url, type, time, name };
                })
              );
              // Newest upload first.
              withMeta.sort((a, b) => b.time - a.time);
              const media = withMeta.map(({ url, type, name }) => ({ url, type, name }));

              setSelected((prevSelected: any) => {
                if (prevSelected?.pk === selected.pk) {
                  return {
                    ...prevSelected,
                    attachments: media.map((m) => m.url),
                    media,
                  };
                }
                return prevSelected;
              });
            }
          })
          .catch((error) => {
            console.log('Error fetching images:', error);
          });
      }
    }
  }, [selected?.pk, isJoined, mediaRefreshKey]);

  const callBack = (item: any) => {
    setActiveRoom(item);
    if (isPhoneViewport()) {
      setSidebarOpen(false);
    }
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  // Phone drawer: mark body so global FAB can hide above the open sidebar stack
  useEffect(() => {
    const className = 'chat-sidebar-open';
    if (sidebarOpen && isPhone) {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }
    return () => {
      document.body.classList.remove(className);
    };
  }, [sidebarOpen, isPhone]);

  // Message thread open: hide global dashboard FAB on phone
  useEffect(() => {
    const className = 'message-thread-open';
    if (showReplies && isPhone) {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }
    return () => {
      document.body.classList.remove(className);
    };
  }, [showReplies, isPhone]);

  // Chat info open: hide global dashboard FAB on phone
  useEffect(() => {
    const className = 'chat-info-open';
    if (chatInfo && isPhone) {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }
    return () => {
      document.body.classList.remove(className);
    };
  }, [chatInfo, isPhone]);

  return {
    showReplies,
    setShowReplies,
    activateRoom,
    setActiveRoom,
    chatInfo,
    setChatInfo,
    showThreads,
    sidebarOpen,
    isPhone,
    isMobile,
    selected,
    setSelected,
    isJoined,
    chat,
    allRooms,
    isLoading,
    hasMore,
    isLoadingMore,
    loadOlderMessages,
    isInitialized,
    handleThreads,
    callBack,
    handleSidebarToggle,
    handleSidebarClose,
  };
}
